import axios from 'axios';
import get from 'lodash/get';
import { config } from 'config';
import { clearAuth, clearSkipLogin, getAuth, saveAuth } from 'service/local-data-provider';
import camelcaseKeys from 'camelcase-keys';
import { isLineBank } from 'config';

export class AccessTokenRevoke extends Error {}
export class PermissionDenied extends Error {}

const prettify = obj => {
  return camelcaseKeys(obj, { deep: true });
};

const API_VERSION = 'v3';

class ApiService {
  http;
  refreshTokenHttp;
  isRefreshing = false;
  waitingRequestQueue = [];

  constructor() {
    this.http = this.createHttp();
    this.http.interceptors.request.use(value => this.requestInterceptor(value));
    this.http.interceptors.response.use(
      value => prettify(value),
      error => this.refreshTokenInterceptor(error)
    );
    this.refreshTokenHttp = this.createHttp();
    this.refreshTokenHttp.interceptors.request.use(value => this.requestInterceptor(value));
    this.refreshTokenHttp.interceptors.response.use(
      value => prettify(value),
      error => error
    );
  }

  bootstraps = () => {
    return this.http.get(`${API_VERSION}/bootstraps`);
  };

  auth = (mobile, code) => {
    return this.http.post(`${API_VERSION}/auth`, {
      mobile,
      code,
      with_email: false,
    });
  };

  register = ({ email, plateNumber, vehicleType, marketingEmailOptedIn }) => {
    return this.http.post(`${API_VERSION}/web_auth`, {
      email,
      plate_number: plateNumber,
      vehicle_type: vehicleType,
      marketing_email_opted_in: marketingEmailOptedIn,
    });
  };

  logout = () => {
    return this.http.post(`${API_VERSION}/logout`);
  };

  changePassword = (oldPassword, newPassword) => {
    return this.http.post(`${API_VERSION}/me/password`, {
      password: oldPassword,
      new_password: newPassword,
    });
  };

  fetchOtp = mobile => {
    return this.http.post(`${API_VERSION}/otp`, {
      mobile,
      need_check_email: false,
    });
  };

  userInfo = () => {
    return this.http.get(`${API_VERSION}/me`);
  };

  updateUserInfo = ({ email, displayName, receipt, enableOnStreetParkingAutoPay, marketingEmailOptedIn }) => {
    let receiptPayload = undefined;
    if (receipt) {
      receiptPayload = {
        ...receipt,
        love_code: receipt.love_code || receipt.loveCode,
        citizen_digital_number: receipt.citizen_digital_number || receipt.citizenDigitalNumber,
      };
    }

    return this.http.post(`${API_VERSION}/me`, {
      email: email,
      display_name: displayName,
      enable_on_street_parking_auto_pay: enableOnStreetParkingAutoPay,
      receipt: receiptPayload,
      marketing_email_opted_in: marketingEmailOptedIn,
    });
  };

  fetchRefuelingRequestsLatest = () => {
    return this.http.get(`${API_VERSION}/refueling_requests/latest`);
  };

  answerRefuelingRequests = (id, answer) => {
    return this.http.post(`${API_VERSION}/refueling_requests/${id}`, { answer });
  };

  fetchStreetParkingBills = (city, plateNumber) => {
    return this.http.get(`${API_VERSION}/on_street_parking_bills`, {
      params: {
        city: city,
        plate_number: plateNumber,
      },
    });
  };

  payStreetParkingBills = (city, codes, plateNumber) => {
    return this.http.post(`${API_VERSION}/on_street_parking_bills/pay`, {
      city: city,
      bill_codes: codes,
      plate_number: plateNumber,
    });
  };

  fetchLicensePlates = () => {
    return this.http.get(`${API_VERSION}/license_plates`);
  };

  addLicensePlate = (plateNumber, vehicleType, energySource) => {
    return this.http.post(`${API_VERSION}/license_plates`, {
      plate_number: plateNumber,
      vehicle_type: vehicleType,
      energy_source: energySource
    });
  };

  deleteLicensePlate = id => {
    return this.http.delete(`${API_VERSION}/license_plates/${id}`);
  };

  bindLicensePlate = id => {
    return this.http.post(`${API_VERSION}/license_plates/${id}/bind_city_parkinglot`);
  };

  modifyLicensePlate = (id, plateNumber, vehicleType, energySource) => {
    return this.http.put(`${API_VERSION}/license_plates/${id}`, {
      plate_number: plateNumber,
      vehicle_type: vehicleType,
      energy_source: energySource
    });
  };

  initMochipayValidation = () => {
    return this.http.post(`${API_VERSION}/wallets/init_mochipay_validation`);
  };

  bindCpcPay = cpcId => {
    return this.http.post(`${API_VERSION}/wallets/bind_cpc_pay`, {
      cpc_id: cpcId,
    });
  };

  getNpcCards = () => {
    return this.http.get(`${API_VERSION}/wallets/get_npc_valid_card_types`);
  };

  bindNpcPay = ({ cardType, enableRedeem }) => {
    return this.http.post(`${API_VERSION}/wallets/bind_npc_pay`, {
      card_type: cardType,
      enable_credit_point_redeem: enableRedeem,
    });
  };

  enableRedeemDiscount = () => {
    return this.http.post(`${API_VERSION}/wallets/redeem_bonus_points/enable`);
  };

  disableRedeemDiscount = () => {
    return this.http.post(`${API_VERSION}/wallets/redeem_bonus_points/disable`);
  };

  bindMochipayWallet = (token, transactionId) => {
    return this.http.post(`${API_VERSION}/wallets/mochipay_wallet`, {
      token: token,
      transaction_id: transactionId,
      default_distributor: true,
    });
  };

  deleteCreditCard = () => {
    return this.http.delete(`${API_VERSION}/wallets/credit_cards`);
  };

  fetchDebitAccountTransactions = (id, startAt, endAt, page, totalPage) => {
    return this.http.get(`${API_VERSION}/debit_accounts/${id}/transactions`, {
      params: {
        start_at: startAt,
        end_at: endAt,
        page: page,
        per_page: totalPage,
      },
    });
  };

  sendDebitCardReport = (id, startAt, endAt) => {
    return this.http.post(`${API_VERSION}/debit_accounts/${id}/transactions/export`, {
      start_at: startAt,
      end_at: endAt,
    });
  };

  fetchTwoFactorAuth = () => {
    return this.http.get(`${API_VERSION}/two_factor_auth`);
  };

  enableTwoFactorAuth = totp => {
    return this.http.post(`${API_VERSION}/two_factor_auth/enable`, {
      totp_code: totp,
    });
  };

  disableTwoFactorAuth = () => {
    return this.http.post(`${API_VERSION}/two_factor_auth/disable`);
  };

  fetchOrders = (page = 1, pageSize = 100, transactionTypes, plateNumber, startAt, endAt) => {
    return this.http.get(`${API_VERSION}/orders/simple`, {
      params: {
        transaction_types: transactionTypes,
        plate_number: plateNumber,
        start_at: startAt,
        end_at: endAt,
        page: page,
        per_page: pageSize,
      },
    });
  };

  fetchOrderDetail = url => {
    return this.http.get(url);
  };

  sendUserReport = (transactionTypes, startAt, endAt, plateNumber) => {
    return this.http(`${API_VERSION}/user_reports`, {
      params: {
        transaction_types: transactionTypes,
        start_at: startAt,
        end_at: endAt,
        plate_number: plateNumber,
      },
    });
  };

  fetchMochiCoins = ({ state, page = 1 }) => {
    return this.http.get(`${API_VERSION}/mochi_coin_transactions`, {
      params: {
        state: state,
        page: page,
        limit: 20,
      },
    });
  };

  fetchParkingRecord = () => {
    return this.http.get(`${API_VERSION}/parking_records`);
  };

  parkingRecordCancel = parkingRecordId => {
    return this.http.post(`${API_VERSION}/parking_records/${parkingRecordId}/cancel`);
  };

  parkingRecordLeave = parkingRecordId => {
    return this.http.post(`${API_VERSION}/parking_records/${parkingRecordId}/leave`);
  };

  agreeTermsOfTaipeiParkinglotService = () => {
    return this.http.post(`${API_VERSION}/me`, {
      agree_terms_of_taipei_city_parkinglot_service: true,
    });
  };

  updatePushToken = token => {
    return this.http.post(`${API_VERSION}/distributor_push_token`, {
      push_token: token,
    });
  };

  createHttp = () =>
    axios.create({
      baseURL: config.apiUrl,
      timeout: 60000,
      validateStatus: status => {
        return status >= 200 && status < 300;
      },
    });

  requestInterceptor = req => {
    const accessToken = this.accessToken();
    if (accessToken) {
      req.headers['Authorization'] = accessToken;
    }
    req.headers['Mochipay-Client-Id'] = config.clientUid;
    return req;
  };

  accessToken = () => {
    const accessToken = get(getAuth(), 'accessToken', undefined);
    return accessToken ? `Bearer ${accessToken}` : '';
  };

  processWaitingQueue = error => {
    this.waitingRequestQueue.forEach(promise => {
      if (error) {
        promise.reject(error);
      } else {
        promise.resolve();
      }
    });
    this.waitingRequestQueue = [];
  };

  refreshTokenInterceptor = error => {
    const {
      config: originalRequest,
      response: { status },
    } = error;
    if (status === 401) {
      if (isLineBank) {
        window.location.href = `https://${window.location.host}/expired`;
        return console.log('oops! seems like token is expired!');
      }

      if (this.isRefreshing) {
        return new Promise((resolve, reject) => {
          this.waitingRequestQueue.push({ resolve, reject });
        })
          .then(() => {
            originalRequest.headers.Authorization = `${this.accessToken()}`;
            return this.http(originalRequest);
          })
          .catch(retryError => {
            return Promise.reject(retryError);
          });
      } else {
        this.isRefreshing = true;
        return new Promise((resolve, reject) => {
          this.refreshTokenHttp
            .post(`/oauth/token`, {
              refresh_token: get(getAuth(), 'refreshToken', ''),
              grant_type: 'refresh_token',
            })
            .then(value => {
              const auth = value.data;
              try {
                saveAuth({
                  ...auth,
                  scopes: auth.scope,
                });
                this.http.defaults.headers.Authorization = `${this.accessToken()}`;
                originalRequest.headers.Authorization = `${this.accessToken()}`;
                resolve(this.http(originalRequest));
                this.processWaitingQueue(null);
              } catch (e) {
                throw e;
              }
            })
            .catch(refreshError => {
              const status = refreshError.response?.status;
              if (status === 401) {
                clearAuth();
                clearSkipLogin();
                reject(new AccessTokenRevoke());
              } else {
                clearAuth();
                clearSkipLogin();
                window.location.href = `https://${window.location.host}/user/welcome`;
                reject(refreshError);
              }
              this.processWaitingQueue(refreshError);
            })
            .finally(() => {
              this.isRefreshing = false;
            });
        });
      }
    } else if (status === 403) {
      return Promise.reject(new PermissionDenied());
    } else {
      return Promise.reject(error);
    }
  };

  boards = codes => {
    return this.http.get(`${API_VERSION}/bulletin_boards`, {
      params: {
        codes,
      },
    });
  };

  fetchOneTimeToken = token => {
    return this.http.post(`${API_VERSION}/one_time_token`, {
      one_time_token: token,
    });
  };

  fetchScanPaymentToken = () => this.http.post(`${API_VERSION}/payment_tokens`);

  /**
      state: ('not_started' | 'available' | 'expired' | 'all_redeemed')[];
      page?: number;
      perPage?: number;
  */
  fetchCoupons = ({ state, page }) =>{
    return this.http.get(`${API_VERSION}/promotion_certificates`, {
      params: {
        state,
        page,
        per_page: 20,
      },
    });

  }

  /**
      code: string;
      redemptionCampaignId?: string;
      plateNumber?: string;
  */
  redeemCoupon = ({ code, redemptionCampaignId, plateNumber }) =>
    this.http.post(`${API_VERSION}/redemption_campaigns/redeem`, {
      redemption_code: code,
      redemption_campaign_id: redemptionCampaignId,
      plate_number: plateNumber,
    }, {
      
    });
}

export default new ApiService();
